import { defineAsyncComponent } from 'vue';

/**
 * vue-pdf-embed treats any plain string `source` as a URL to fetch, even a
 * data: URL - which routes it through pdf.js's network layer instead of
 * decoding it as inline bytes, and silently produces no document. Freshly
 * picked (not yet uploaded) attachments use data: URLs from FileReader, so
 * decode those into pdf.js's documented `data` (byte array) input instead.
 * Real URLs (http(s):, blob:) pass through unchanged.
 */
export function toPdfSource(source: string): string | { data: Uint8Array } {
  if (!source?.startsWith('data:')) return source;
  const base64 = source.slice(source.indexOf(',') + 1);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return { data: bytes };
}

/**
 * Shared lazy-loaded PDF viewer component. Loads the "essential" build
 * (no bundled worker, no eval-dependent legacy pdf.js code) and points
 * pdf.js's worker at a same-origin copy (see webpack.config.js's
 * CopyPlugin) instead of the default's blob: URL, so rendering works
 * under a CSP without 'unsafe-eval' or 'blob:' in worker-src.
 */
export const VuePdfEmbed = defineAsyncComponent(async () => {
  const { default: Component, GlobalWorkerOptions } = await import(
    'vue-pdf-embed/dist/index.essential.mjs'
  );
  GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  return Component;
});
